import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Button from "./Button";
import ThemeToggle from "./ThemeToggle";
import SignUpModal from "./SignUpModal";

const navItems = [
  { name: "Features", href: "#features" },
  { name: "Services", href: "#services" },
  { name: "Integrations", href: "#integrations" },
  { name: "Pricing", href: "#pricing" },
  { name: "FAQ", href: "#faq" },
];

const Header = ({ onGetStarted }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleClick = (e) => {
    e.preventDefault();
    onGetStarted();
    setIsMenuOpen(false); // Close mobile menu if open
  };

  return (
    <header className={`fixed top-0 left-0 w-full z-40 transition-all duration-200 ${
      isScrolled ? 'bg-white/80 dark:bg-[#030B17]/80 backdrop-blur-xl' : ''
    }`}>
      <div className="container mx-auto px-4">
        <nav className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#hero" className="flex items-center gap-3">
            <img 
              src="/intellishift-symbol.svg" 
              width={32} 
              height={32} 
              alt="IntelliShift" 
              className="w-8 h-8" 
            />
            <span className="text-gray-900 dark:text-white text-xl font-semibold">IntelliShift</span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <motion.a
                key={item.name}
                href={item.href}
                className="text-gray-600 hover:text-gray-900 dark:text-white/80 dark:hover:text-white transition-colors"
                whileHover={{ y: -2 }}
                whileTap={{ y: 0 }}
              >
                {item.name}
              </motion.a>
            ))}
            <div className="flex items-center gap-6">
              <ThemeToggle />
              <Button className="cursor-pointer" href="#" onClick={handleClick}>
                Get Started
              </Button>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            whileTap={{ scale: 0.9 }}
            className="block md:hidden text-gray-900 dark:text-white"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 12H21"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M3 6H21"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M3 18H21"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </motion.button>

          {/* Mobile Menu */}
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="fixed inset-0 z-50 min-h-screen bg-white/90 dark:bg-[#030B17]/90 backdrop-blur-xl"
              >
                {/* Background Gradients */}
                <div className="absolute inset-0 pointer-events-none overflow-hidden">
                  {/* Top gradient */}
                  <div className="absolute top-0 left-1/4 w-[70rem] h-[40rem] bg-gradient-to-r from-indigo-500/20 via-purple-500/20 to-pink-500/20 blur-[128px] -translate-y-1/2" />
                  {/* Bottom gradient */}
                  <div className="absolute bottom-0 right-1/4 w-[70rem] h-[40rem] bg-gradient-to-r from-pink-500/20 via-purple-500/20 to-indigo-500/20 blur-[128px] translate-y-1/2" />
                </div>

                {/* Close Button */}
                <div className="absolute top-6 right-6">
                  <motion.button
                    onClick={() => setIsMenuOpen(false)}
                    className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </motion.button>
                </div>

                {/* Menu Content */}
                <div className="flex flex-col items-center justify-center min-h-screen p-8">
                  {/* Navigation Links */}
                  <div className="flex flex-col items-center gap-8 mb-12">
                    {navItems.map((item, index) => (
                      <motion.a
                        key={item.name}
                        href={item.href}
                        onClick={() => setIsMenuOpen(false)}
                        className="text-2xl font-medium text-gray-900 dark:text-white hover:text-primary transition-colors"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        {item.name}
                      </motion.a>
                    ))}
                  </div>

                  {/* Action Buttons */}
                  <motion.div
                    className="flex flex-col items-center gap-4"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: navItems.length * 0.1 }}
                  >
                    <ThemeToggle />
                    <Button className="cursor-pointer" href="#" onClick={handleClick}>
                      Get Started
                    </Button>
                  </motion.div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </nav>
      </div>

      {/* Sign Up Modal */}
      <SignUpModal isOpen={false} onClose={() => {}} />
    </header>
  );
};

export default Header;
